import React, { useState } from 'react';
import { Button, Card, Col, Input, InputNumber, Row, Space, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ProposalFormCategory, ProposalFormField } from '../../types/proposal';
import { ProposalFormFieldRow } from './ProposalFormFieldRow';

const { Text } = Typography;

interface ProposalFormSectionProps {
  category: ProposalFormCategory;
  subtotal: number;
  formatCurrency: (value: number) => string;
  readOnly?: boolean;
  onFieldChange?: (field: ProposalFormField, value: number | null) => void;
  onFieldLabelChange?: (field: ProposalFormField, label: string) => void;
  onFieldNoteChange?: (field: ProposalFormField, note: string | null) => void;
  onAddCustomField?: (label: string, value: number | null) => void;
}

export const ProposalFormSection: React.FC<ProposalFormSectionProps> = ({
  category,
  subtotal,
  formatCurrency,
  readOnly = false,
  onFieldChange,
  onFieldLabelChange,
  onFieldNoteChange,
  onAddCustomField,
}) => {
  const [addingCustom, setAddingCustom] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customValue, setCustomValue] = useState<number | null>(null);
  const canAddCustom = !readOnly && Boolean(onAddCustomField);
  const canConfirmCustom = customLabel.trim().length > 0;

  const resetCustomDraft = () => {
    setCustomLabel('');
    setCustomValue(null);
    setAddingCustom(false);
  };

  const handleConfirmCustom = () => {
    if (!onAddCustomField || !canConfirmCustom) return;
    onAddCustomField(customLabel.trim(), customValue);
    resetCustomDraft();
  };

  return (
    <Card title={category.title} style={{ marginBottom: 16 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {category.fields.map(field => (
          <ProposalFormFieldRow
            key={field.id}
            field={field}
            readOnly={readOnly}
            onChange={value => onFieldChange?.(field, value)}
            onLabelChange={label => onFieldLabelChange?.(field, label)}
            onNoteChange={note => onFieldNoteChange?.(field, note)}
          />
        ))}
        {canAddCustom && (
          <Card size="small" hoverable>
            {addingCustom ? (
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Row gutter={16} align="middle">
                  <Col flex="auto">
                    <Space direction="vertical" size={2}>
                      <Input
                        value={customLabel}
                        placeholder="Custom item name"
                        onChange={event => setCustomLabel(event.target.value)}
                      />
                      <Space size="small">
                        <Button
                          size="small"
                          type="primary"
                          onClick={handleConfirmCustom}
                          disabled={!canConfirmCustom}>
                          OK
                        </Button>
                        <Button size="small" onClick={resetCustomDraft}>
                          Cancel
                        </Button>
                      </Space>
                    </Space>
                  </Col>
                  <Col flex="120px" />
                  <Col flex="200px">
                    <InputNumber
                      min={0}
                      value={customValue ?? null}
                      onChange={value => setCustomValue(typeof value === 'number' ? value : null)}
                      placeholder="Price"
                      style={{ width: '100%' }}
                    />
                  </Col>
                </Row>
              </Space>
            ) : (
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  <Button type="link" icon={<PlusOutlined />} onClick={() => setAddingCustom(true)}>
                    Add custom item
                  </Button>
                </Col>
                <Col flex="120px" />
                <Col flex="200px" />
              </Row>
            )}
          </Card>
        )}
        <Row justify="end" align="middle">
          <Col>
            <Text type="secondary" style={{ marginRight: 12 }}>
              Subtotal
            </Text>
          </Col>
          <Col>
            <Text strong>{formatCurrency(subtotal)}</Text>
          </Col>
        </Row>
      </Space>
    </Card>
  );
};
